import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import type { CurrencyCode } from 'app/api/services/site-configs/types';
import type { ReduxState } from 'app/redux/types';

import { CURRENCY_BY_CODE } from 'app/constants/locales';
import { tFunction, useTranslation } from 'app/lang';
import { accountFirstName, accountLastName } from 'app/lib/account/selectors';
import { StakeType } from 'app/lib/staking/constants';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';
import selectCurrency from 'app/redux/selectors/сurrency';

import NoFullNameLink from '../components/NoFullNameLink';
import { DISABLED_TITLE_STATUSES, FreebetPossibleStatus, REASON_BY_POSSIBLE_STATUS } from '../constants';
import type { RecordOfEntryLimitsType, RecordOfPaymentOptionsEntry } from '../records';
import { selectCurrentStakeType } from '../selectors/stakeTypes';
import { selectExpressLimits, selectExpressPaymentOptions } from '../selectors/stakeTypes/express';

type DisabledTitles = {
  pledgeStakes?: string;
  freeBets?: ReactNode;
};

const useStakeTypesDisabledTitles = (): DisabledTitles => {
  const { t } = useTranslation();
  const expressLimits = useSelector(selectExpressLimits);
  const expressPaymentOptions = useSelector(selectExpressPaymentOptions);
  const currency = useSelector(selectCurrency);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const stakeType = useSelector((state: ReduxState) => selectCurrentStakeType(state).toArray()[0]);
  const hasFullName = useSelector((state: ReduxState) => {
    return Boolean(accountFirstName(state) && accountLastName(state));
  });

  switch (stakeType) {
    case StakeType.SINGLE:
      return {
        pledgeStakes: t(DISABLED_TITLE_STATUSES.noChainAvailableForSingleGroup),
        freeBets: t(DISABLED_TITLE_STATUSES.noFreebetAvailableForSingleGroup),
      };
    case StakeType.EXPRESS:
      return {
        pledgeStakes: t(DISABLED_TITLE_STATUSES.noExpressChainAvailable),
        freeBets: getFreeBetsTitle(
          t,
          isLoggedIn,
          hasFullName,
          currency,
          expressLimits,
          expressPaymentOptions,
        ),
      };
    case StakeType.SYSTEM:
      return {
        pledgeStakes: t(DISABLED_TITLE_STATUSES.noSystemChainAvailable),
        freeBets: t(REASON_BY_POSSIBLE_STATUS[FreebetPossibleStatus.SYSTEM_STAKE_IS_FORBIDDEN]),
      };
    default:
      assertExhaustiveness(stakeType);
  }
};

function getFreeBetsTitle(
  t: tFunction,
  isLoggedIn: boolean,
  hasFullName: boolean,
  currency: CurrencyCode,
  limits: RecordOfEntryLimitsType,
  paymentOptions: RecordOfPaymentOptionsEntry,
) {
  const maxValue = limits.max;
  const minValue = limits.min;
  const { onlyCurrency, noExpressFreebetAvailable } = DISABLED_TITLE_STATUSES;

  if (maxValue < minValue) {
    return t(onlyCurrency, { currency: CURRENCY_BY_CODE[currency] });
  }

  if (!paymentOptions?.isFreeBetPossible) {
    return t(noExpressFreebetAvailable);
  }

  if (isLoggedIn && !hasFullName) {
    return <NoFullNameLink />;
  }
}

export default useStakeTypesDisabledTitles;
